import { Injectable } from '@angular/core';
import { Auth } from '@aws-amplify/auth';
import { NbToastrService, NbWindowRef, NbWindowService } from '@nebular/theme';
import { Store } from '@ngxs/store';
import { CampaignWebsocketWindowComponent } from 'src/app/features/campaigns/campaign-table/campaign-websocket-window/campaign-websocket-window.component';
import { InsightWebsocketWindowComponent } from 'src/app/features/insights/insights-table/insight-websocket-window/insight-websocket-window.component';
import { AmplifyAPI } from 'src/app/models/amplifyApi';
import { STATUSES } from 'src/app/shared/constants/general-constants';
import { CONFIG } from "../../../environments/environment";
import { AddCampaignJob, RemoveCampaignJob } from '../../features/campaigns/redux/campaign.actions';
import { AddInsightJob, RemoveInsightJob } from '../../features/insights/redux/insights.actions';

type ResourceType = 'campaign' | 'insight';

interface RefEntry {
  job_id: string;
  status: string;
  windowRef: NbWindowRef<any> | null;
  campaignId?: string;
  insightId?: string;
  insightJob: string | null;
}

interface WebSocketResults {
  status?: string;
  jobId?: string;
  job_id?: string;
  [key: string]: any;
}

@Injectable()
export abstract class BaseWebSocketService {
  public ws: WebSocket | null = null;
  public refArray: RefEntry[] = [];

  protected constructor(
    protected readonly type: ResourceType,
    protected readonly toastrService: NbToastrService,
    protected readonly windowService: NbWindowService,
    protected readonly store: Store
  ) {}

  public async setUp(): Promise<void> {
    try {
      const session = await Auth.currentSession();
      const token = session.getAccessToken().getJwtToken();

      const api = CONFIG.API.endpoints.find((obj: AmplifyAPI) => obj.name === 'web_socket');

      if (!api?.endpoint) {
        this.showDanger($localize`WebSocket endpoint is not configured`);
        return;
      }

      this.webSocket(token, api.endpoint);
    } catch {
      this.showDanger($localize`Unable to initialize WebSocket`);
    }
  }

  public webSocket(token: string, url: string): void {
    this.ws = new WebSocket(`${url}?token=${token}`);

    this.ws.onerror = () => {
      this.showDanger($localize`WebSocket Error - Contact support if persistent`);
    };

    this.ws.onclose = () => {};

    this.ws.onmessage = (event: MessageEvent) => {
      this.handleMessage(event);
    };
  }

  public sendMessage(message: string, item?): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      this.showDanger($localize`WebSocket is not connected`);
      return;
    }

    let obj: RefEntry;

    if (this.type === 'campaign') {
      obj = {
        campaignId: item,
        job_id: message,
        status: '',
        windowRef: null,
        insightJob: null
      };

      this.refArray.push(obj);
      this.saveToRedux(obj);
      this.ws.send(JSON.stringify({ action: 'getstatus', jobId: message, campaign_id: item }));
    } else {
      obj = {
        insightId: item,
        job_id: message,
        status: '',
        windowRef: null,
        insightJob: null
      };

      this.refArray.push(obj);
      this.saveToRedux(obj);
      this.ws.send(JSON.stringify({ action: 'getstatus', jobId: message, insight_id: item }));
    }
  }

  public saveToRedux(obj: RefEntry): void {
    const job = { ...obj };

    if (this.type === 'campaign') {
      this.store.dispatch(new AddCampaignJob(job));
    } else {
      this.store.dispatch(new AddInsightJob(job));
    }
  }

  public removeFromRedux(jobId: string): void {
    if (this.type === 'campaign') {
      this.store.dispatch(new RemoveCampaignJob(jobId));
    } else {
      this.store.dispatch(new RemoveInsightJob(jobId));
    }
  }

  public closeWindow(jobId: string): void {
    const index = this.refArray.findIndex((element) => element.job_id === jobId);

    if (index !== -1 && this.refArray[index].windowRef) {
      this.refArray[index].windowRef?.close();
    }

    this.refArray = this.refArray.filter((element) => element.job_id !== jobId);
    this.removeFromRedux(jobId);

    this.toastrService.show('', $localize`Successfully stopped ${jobId}`, {
      status: 'success'
    });
  }

  private handleMessage(event: MessageEvent): void {
    let results: WebSocketResults;

    try {
      results = JSON.parse(event.data);
    } catch {
      this.showDanger($localize`Invalid WebSocket message received`);
      return;
    }

    if (!results.status) {
      return;
    }

    let index = -1;

    if (results.status === STATUSES.SUCCESS || results.status === STATUSES.FAIL) {
      index = this.refArray.findIndex((element) => element.job_id === results.jobId);
      if (results.jobId) {
        this.removeFromRedux(results.jobId);
      }
    } else {
      index = this.refArray.findIndex((element) => element.job_id === results.job_id);
    }

    if (index === -1) {
      return;
    }

    this.refArray[index].status = results.status;
    this.openOrUpdateWindow(index, results);
  }

  private openOrUpdateWindow(index: number, results: WebSocketResults): void {
    this.refArray[index].windowRef?.close();

    if (this.type === 'campaign') {
      this.refArray[index].windowRef = this.windowService.open(CampaignWebsocketWindowComponent, {
        title: `${this.refArray[index].campaignId} - ${results.status}`,
        context: { results }
      });
    } else {
      this.refArray[index].windowRef = this.windowService.open(InsightWebsocketWindowComponent, {
        title: `${this.refArray[index].insightId} - ${results.status}`,
        context: { results }
      });
    }

    if (results.status !== STATUSES.SUCCESS && results.status !== STATUSES.FAIL) {
      this.refArray[index].windowRef?.minimize();
    }
  }

  private showDanger(message: string): void {
    this.toastrService.show('', message, { status: 'danger' });
  }
}

@Injectable({ providedIn: 'root' })
export class CampaignWebSocketService extends BaseWebSocketService {
  constructor(toastrService: NbToastrService, windowService: NbWindowService, store: Store) {
    super('campaign', toastrService, windowService, store);
  }
}

@Injectable({ providedIn: 'root' })
export class InsightWebSocketService extends BaseWebSocketService {
  constructor(toastrService: NbToastrService, windowService: NbWindowService, store: Store) {
    super('insight', toastrService, windowService, store);
  }
}
